// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ellipsis, mergeWithCss, parseBoolean } from "@conklin/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import parse from "html-react-parser";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductDescriptionStyles;
    // Xng-Change
    query?: string; // Parse auto complete search query to make a different style for substring matching query
}

type Props = OwnProps;

export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        color: "text.main",
        css: css`
            width: auto;
            align-items: flex-start;
            align-self: baseline;
            margin-bottom: 8px;
        `,
        typographyProps: {
            lineHeight: "16px",
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
                ${ellipsis(2)}
            `,
        },
    },
};

const ProductDescription = ({ product, onClick, extendedStyles, query }: Props) => {
    const [styles] = useState(() => mergeWithCss(productDescriptionStyles, extendedStyles));

    let description =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.description : "");
    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;

    if (query) {
        description = description.replace(query.toUpperCase(), `<b>${query.toUpperCase()}</b>`);
    }
    // XNG-Changes: isRestricted
    const isRestricted = "product" in product ? false : parseBoolean(product.properties.isRestrictedProduct);
    return productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) && !isRestricted ? (
        <Link
            {...styles.productDetailLink}
            href={productDetailPath}
            alt={`See product details for ${description}`}
            onClick={onClick}
            data-test-selector="productDescriptionLink"
            aria-label={`See product details for ${description}`}
        >
            {query ? parse(description, parserOptions) : description}
        </Link>
    ) : (
        <Typography {...styles.productDetailDescription}>
            {query ? parse(description, parserOptions) : description}
        </Typography>
    );
};

ProductDescription.defaultProps = {
    query: "",
};

export default ProductDescription;
